import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { PageSkeleton } from '../components/PageSkeleton';
import { NETWORK_ICONS } from '../utils/networkIcons';
import type { CalendarEvent, PublishStatus } from '@agency-smm/shared';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const MonthLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  min-width: 180px;
  text-align: center;
`;

const NavButton = styled.button`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text};
  transition: background-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
`;

const DayHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  text-align: center;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-bottom: ${({ theme }) => theme.borders.thin};
`;

const DayCell = styled.div<{ $isToday?: boolean; $isOtherMonth?: boolean }>`
  min-height: 100px;
  padding: ${({ theme }) => theme.spacing.xs};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $isToday, theme }) => $isToday ? `${theme.colors.primary}08` : 'transparent'};
  opacity: ${({ $isOtherMonth }) => $isOtherMonth ? 0.4 : 1};

  &:nth-child(7n) {
    border-right: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: 60px;
  }
`;

const DayNumber = styled.span<{ $isToday?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $isToday, theme }) => $isToday ? theme.fontWeight.bold : theme.fontWeight.medium};
  color: ${({ $isToday, theme }) => $isToday ? 'white' : theme.colors.text};
  background: ${({ $isToday, theme }) => $isToday ? theme.colors.primary : 'transparent'};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-bottom: 2px;
`;

const EventPill = styled.div<{ $status: PublishStatus }>`
  padding: 2px 6px;
  margin-bottom: 2px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.15s;
  background: ${({ $status }) => {
    switch ($status) {
      case 'scheduled': return '#3B82F620';
      case 'publishing': return '#F59E0B20';
      case 'published': return '#10B98120';
      case 'failed': return '#EF444420';
      default: return '#6B728020';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'scheduled': return '#3B82F6';
      case 'publishing': return '#F59E0B';
      case 'published': return '#10B981';
      case 'failed': return '#EF4444';
      default: return '#6B7280';
    }
  }};
  border-left: 3px solid ${({ $status }) => {
    switch ($status) {
      case 'scheduled': return '#3B82F6';
      case 'publishing': return '#F59E0B';
      case 'published': return '#10B981';
      case 'failed': return '#EF4444';
      default: return '#6B7280';
    }
  }};

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 10px;
    padding: 1px 4px;
  }
`;

const Legend = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $color }) => $color};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
`;

// Event detail sidebar
const EventDetailOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 360px;
  background: ${({ theme }) => theme.colors.surface};
  border-left: ${({ theme }) => theme.borders.thin};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: 50;
  padding: ${({ theme }) => theme.spacing.lg};
  overflow-y: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const DetailTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const DetailImage = styled.img`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const DetailContent = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  white-space: pre-wrap;
`;

const DetailMeta = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const StatusBadge = styled.span<{ $status: PublishStatus }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  background: ${({ $status }) => {
    switch ($status) {
      case 'scheduled': return '#3B82F620';
      case 'published': return '#10B98120';
      case 'failed': return '#EF444420';
      default: return '#6B728020';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'scheduled': return '#3B82F6';
      case 'published': return '#10B981';
      case 'failed': return '#EF4444';
      default: return '#6B7280';
    }
  }};
`;

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

export function CalendarPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());

  const monthString = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;

  const fetchEvents = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      const data = await apiClient.get<CalendarEvent[]>(
        `/api/workspaces/${workspaceId}/calendar?month=${monthString}`
      );
      setEvents(data);
    } catch (error) {
      console.error('Failed to load calendar events:', error);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, monthString]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  const goToToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };

  // Build calendar grid cells
  const calendarCells = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const prevMonthDays = getDaysInMonth(currentYear, currentMonth - 1);

    const cells: { day: number; isOtherMonth: boolean; dateStr: string }[] = [];

    // Previous month trailing days
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      const m = currentMonth === 0 ? 12 : currentMonth;
      const y = currentMonth === 0 ? currentYear - 1 : currentYear;
      cells.push({ day: d, isOtherMonth: true, dateStr: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({
        day: d,
        isOtherMonth: false,
        dateStr: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      });
    }

    // Next month leading days to fill grid
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        const m = currentMonth + 2 > 12 ? 1 : currentMonth + 2;
        const y = currentMonth + 2 > 12 ? currentYear + 1 : currentYear;
        cells.push({ day: d, isOtherMonth: true, dateStr: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
      }
    }

    return cells;
  }, [currentYear, currentMonth]);

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const event of events) {
      const dateKey = event.scheduledFor.slice(0, 10);
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(event);
    }
    return map;
  }, [events]);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const monthLabel = new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' });

  if (isLoading && events.length === 0) {
    return <PageSkeleton />;
  }

  return (
    <>
      <PageHeader>
        <Title>Content Calendar</Title>
        <MonthNav>
          <Button $variant="outline" $size="sm" onClick={goToToday}>Today</Button>
          <NavButton onClick={prevMonth}>&larr;</NavButton>
          <MonthLabel>{monthLabel}</MonthLabel>
          <NavButton onClick={nextMonth}>&rarr;</NavButton>
        </MonthNav>
      </PageHeader>

      {events.length === 0 && !isLoading ? (
        <EmptyState>
          <p>No scheduled posts this month.</p>
          <p style={{ marginTop: '8px', fontSize: '13px' }}>
            Go to the Generate page to create posts, then schedule them from the post actions.
          </p>
        </EmptyState>
      ) : null}

      <CalendarGrid>
        {DAY_NAMES.map(d => (
          <DayHeader key={d}>{d}</DayHeader>
        ))}
        {calendarCells.map((cell, i) => {
          const dayEvents = eventsByDate[cell.dateStr] || [];
          const isToday = cell.dateStr === todayStr;
          return (
            <DayCell key={i} $isToday={isToday} $isOtherMonth={cell.isOtherMonth}>
              <DayNumber $isToday={isToday}>{cell.day}</DayNumber>
              {dayEvents.slice(0, 3).map(evt => (
                <EventPill
                  key={evt.id}
                  $status={evt.publishStatus}
                  onClick={() => setSelectedEvent(evt)}
                  title={evt.content.slice(0, 100)}
                >
                  {NETWORK_ICONS[evt.network] || evt.network} {evt.content.slice(0, 20)}
                </EventPill>
              ))}
              {dayEvents.length > 3 && (
                <EventPill $status="draft" onClick={() => setSelectedEvent(dayEvents[3])}>
                  +{dayEvents.length - 3} more
                </EventPill>
              )}
            </DayCell>
          );
        })}
      </CalendarGrid>

      <Legend>
        <LegendItem><LegendDot $color="#6B7280" /> Draft</LegendItem>
        <LegendItem><LegendDot $color="#3B82F6" /> Scheduled</LegendItem>
        <LegendItem><LegendDot $color="#F59E0B" /> Publishing</LegendItem>
        <LegendItem><LegendDot $color="#10B981" /> Published</LegendItem>
        <LegendItem><LegendDot $color="#EF4444" /> Failed</LegendItem>
      </Legend>

      {selectedEvent && (
        <EventDetailOverlay>
          <DetailHeader>
            <DetailTitle>Post Details</DetailTitle>
            <CloseButton onClick={() => setSelectedEvent(null)}>&times;</CloseButton>
          </DetailHeader>
          {selectedEvent.imageUrl && (
            <DetailImage src={selectedEvent.imageUrl} alt="Post" />
          )}
          <DetailMeta>
            <StatusBadge $status={selectedEvent.publishStatus}>
              {selectedEvent.publishStatus}
            </StatusBadge>
            {' '} &middot; {NETWORK_ICONS[selectedEvent.network] || selectedEvent.network}
            {' '} &middot; {new Date(selectedEvent.scheduledFor).toLocaleString()}
          </DetailMeta>
          <DetailContent>{selectedEvent.content}</DetailContent>
          {selectedEvent.publishStatus === 'scheduled' && (
            <Button $size="sm" $variant="outline" onClick={() => setSelectedEvent(null)}>
              Edit Schedule
            </Button>
          )}
        </EventDetailOverlay>
      )}
    </>
  );
}
